// =============================================================================
// SimSense Platform — Toast Notification System
// =============================================================================

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

type ToastType = "success" | "error" | "info";

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    const duration = type === "error" ? 5000 : 3000;
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const value: ToastContextValue = {
    success: useCallback((msg: string) => addToast(msg, "success"), [addToast]),
    error: useCallback((msg: string) => addToast(msg, "error"), [addToast]),
    info: useCallback((msg: string) => addToast(msg, "info"), [addToast]),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div style={{
        position: "fixed",
        top: "60px",
        right: "16px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        pointerEvents: "none",
      }}>
        {toasts.map(t => (
          <div
            key={t.id}
            style={{
              pointerEvents: "auto",
              padding: "12px 20px",
              borderRadius: "var(--radius-sm, 8px)",
              background: t.type === "success" ? "rgba(34, 197, 94, 0.15)"
                : t.type === "error" ? "rgba(239, 68, 68, 0.15)"
                : "rgba(59, 130, 246, 0.15)",
              border: `1px solid ${
                t.type === "success" ? "var(--good, #22c55e)"
                : t.type === "error" ? "var(--bad, #ef4444)"
                : "var(--accent, #3b82f6)"
              }`,
              color: "var(--text1, #e2e8f0)",
              fontSize: "14px",
              fontWeight: 500,
              maxWidth: "380px",
              boxShadow: "var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.4))",
              backdropFilter: "blur(12px)",
              animation: "toast-in 200ms ease-out",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span style={{ fontSize: "16px", flexShrink: 0 }}>
              {t.type === "success" ? "\u2713" : t.type === "error" ? "\u2717" : "\u2139"}
            </span>
            {t.message}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
